/**
 * Auth storage utilities for Omni Ad.
 * Uses Supabase Auth — session is stored by Supabase in localStorage.
 * We expose the same interface so coworkApi.ts continues working unchanged.
 */

import { supabase } from '@/lib/supabase';

/** User shape compatible with the rest of the app */
export interface AuthUser {
  user_id: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  phone_is_verified?: boolean;
  language?: string;
  want_email_notifications?: boolean;
  want_sms_notifications?: boolean;
  is_client?: boolean;
  is_influencer?: boolean;
  team?: { name?: string; team_id?: string };
  client?: { company_name?: string };
  [key: string]: unknown;
}

export interface AuthStorage {
  token?: string;
  user?: AuthUser;
  start_url?: string;
  code?: string;
  target?: string;
  language?: string;
}

const AUTH_KEY = 'auth';

export function getAuthFromStorage(): AuthStorage {
  try {
    const authData = localStorage.getItem(AUTH_KEY);
    return authData ? (JSON.parse(authData) as AuthStorage) : {};
  } catch {
    return {};
  }
}

export function updateAuthInStorage(updates: Partial<AuthStorage>): void {
  const currentAuth = getAuthFromStorage();
  const updatedAuth = { ...currentAuth, ...updates };
  localStorage.setItem(AUTH_KEY, JSON.stringify(updatedAuth));
}

export function clearAuthFromStorage(): void {
  localStorage.removeItem(AUTH_KEY);
}

/**
 * Get the access token for API calls.
 * Reads from Supabase session first, falls back to legacy storage.
 */
export function getTokenFromStorage(): string | undefined {
  // Try Supabase session stored by @supabase/supabase-js
  try {
    const storageKey = Object.keys(localStorage).find((k) =>
      k.startsWith('sb-') && k.endsWith('-auth-token')
    );
    if (storageKey) {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const session = JSON.parse(raw);
        const token = session?.access_token || session?.currentSession?.access_token;
        if (token) return token;
      }
    }
  } catch {}

  // Fallback to legacy auth storage
  const auth = getAuthFromStorage();
  return auth.token;
}

export function getUserFromStorage(): AuthUser | undefined {
  const auth = getAuthFromStorage();
  return auth.user;
}

/**
 * Clear auth storage and sign out of Supabase, then redirect to /login.
 */
export async function logout(): Promise<void> {
  clearAuthFromStorage();
  await supabase.auth.signOut();
  window.location.href = '/login';
}
